import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  faqTypeOptions,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import AddEditFaq from "./AddEditFaq";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import FaqActionButton from "./FaqActionButton";
import Confirmation from "../../../components/Confirmation";
import { CaretDownIcon, CaretRightIcon } from "@radix-ui/react-icons";
import ExpandFAQ from "./ExpandFAQ";

const FAQs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [faqsList, setFaqsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [faqId, setFaqId] = useState(null);
  const [showAddEditFaq, setShowAddEditFaq] = useState(false);
  const [showFaqDelete, setShowFaqDelete] = useState(false);

  const toggleShowAddEditFaq = () => {
    if (showAddEditFaq) {
      setEdit(false);
      setFaqId(null);
    }
    setShowAddEditFaq(!showAddEditFaq);
  };

  const toggleShowDelete = () => {
    if (showFaqDelete) {
      setFaqId(null);
    }
    setShowFaqDelete(!showFaqDelete);
  };

  const fetchFaq = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_FAQ_LIST}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const faqsList = data.data.data;
        setFaqsList(faqsList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setFaqsList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField]
  );

  const faqsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: faqsList[i]?.id,
        id: faqsList[i]?.id,
        order: faqsList[i]?.order ? faqsList[i]?.order : "",
        question: faqsList[i]?.question ? faqsList[i]?.question : "",
        answer: faqsList[i]?.answer ? faqsList[i]?.answer : "",
        type: faqsList[i]?.type ? faqTypeOptions?.find((item) => item.value === faqsList[i]?.type)?.label : "",
        module: faqsList[i]?.module ? faqsList[i]?.module?.name : "",
        action: (
          <>
            <FaqActionButton
              id={faqsList[i]?.id}
              setEdit={setEdit}
              is_used={faqsList[i]?.is_used}
              setFaqId={setFaqId}
              toggleShowAddEditFaq={toggleShowAddEditFaq}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const faqs = faqsGenerator(faqsList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "order",
      text: "Order",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "question",
      text: "Question",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "type",
      text: "Type",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "module",
      text: "Module",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchFaq();
  }, [fetchFaq]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchFaq(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchFaq(limit, page);
  };

  // Single Faq Delete
  const handleFaqDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.GET_ADMIN_FAQ_LIST}/${faqId}`);
      setLoading(false);
      fetchFaq();
    } catch (error) {
      setLoading(false);
    }
  };

    const expandRow = {
    renderer: (row) => (
      <>
        <ExpandFAQ answerData={row?.answer} />
      </>
    ),
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandByColumnOnly: true,
    headerClasses: THClasses,
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <CaretDownIcon />;
      }
      return <CaretRightIcon />;
    },
    className: TDClasses,
    expandHeaderColumnRenderer: () => {
      return <></>;
    },
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Add Faq */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit FAQ" : "Add FAQ"}
              handleOpen={() => {}}
              handleClose={() => {}}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add FAQ</span>
                </div>
              }
              className={"sm:max-w-full"}
              open={showAddEditFaq}
              buttonClassName={""}
              setOpen={setShowAddEditFaq}
            >
              <AddEditFaq
                edit={edit}
                toggleShowAddEditFaq={toggleShowAddEditFaq}
                id={faqId}
                fetchFaqData={() => fetchFaq(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={faqs}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              expandRow={expandRow}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete FAQ"}
        open={showFaqDelete}
        setOpen={setShowFaqDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete faq?"}
        okAction={async () => {
          handleFaqDelete();
          toggleShowDelete();
          fetchFaq();
        }}
        cancelAction={() => {
          setFaqId(null);
          setShowFaqDelete(!showFaqDelete);
        }}
      />
    </>
  );
};

export default FAQs;
